import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import ScrollReveal from '../components/ScrollReveal';
import defaultImages from '../../images.json';

export default function Home() {
  const [galleryItems, setGalleryItems] = useState(defaultImages.slice(0, 6));
  const [reviews, setReviews] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const reviewsRef = useRef(null);

  const defaultReviews = [
    {
      overskrift: 'Sildebensparket i topklasse',
      navn: 'Henrik Sørensen',
      by: 'Odense NV',
      tekst: 'Vi fik lagt nyt sildebensparket i hele stuen. Snedkerpræcision i absolut topklasse. Venlige håndværkere, der ryddede op efter sig hver dag.',
      stjerner: 5
    },
    {
      overskrift: 'Montering af nyt køkken',
      navn: 'Mette & Lars Kjeldsen',
      by: 'Munkebo',
      tekst: 'Super professionel montering af nyt Kvik køkken. Alt blev udført til tiden og den aftalte pris holdt 100%. Kan varmt anbefales.',
      stjerner: 5
    },
    {
      overskrift: 'Nyt tag og efterisolering',
      navn: 'Christian Lindberg',
      by: 'Svendborg',
      tekst: 'Fik skiftet tag og efterisoleret loftet. Kommunikationen var god hele vejen igennem, og resultatet står snorlige.',
      stjerner: 5
    },
    {
      overskrift: 'Træterrasse og facade',
      navn: 'Kasper Vestergaard',
      by: 'Nyborg',
      tekst: 'Fremragende arbejde på vores træterrasse og facadebeklædning. Flot finish og fantastisk rådgivning omkring materialevalg.',
      stjerner: 5
    }
  ];

  const handlePrevReviews = () => {
    if (reviewsRef.current) {
      reviewsRef.current.scrollBy({ left: -380, behavior: 'smooth' });
    }
  };

  const handleNextReviews = () => {
    if (reviewsRef.current) {
      reviewsRef.current.scrollBy({ left: 380, behavior: 'smooth' });
    }
  };

  // Hero form state
  const [heroForm, setHeroForm] = useState({
    navn: '',
    telefon: '',
    email: '',
    ydelse: 'Tag & Facade',
    beskrivelse: '',
    website_url: ''
  });
  const [heroSubmitting, setHeroSubmitting] = useState(false);
  const [heroSubmitted, setHeroSubmitted] = useState(false);
  const [heroError, setHeroError] = useState('');

  const handleHeroFormChange = (e) => {
    setHeroForm({ ...heroForm, [e.target.name]: e.target.value });
  };

  const handleHeroFormSubmit = async (e) => {
    e.preventDefault();
    setHeroError('');

    if (!heroForm.navn || !heroForm.telefon || !heroForm.email) {
      setHeroError('Udfyld venligst navn, telefon og e-mail.');
      return;
    }

    setHeroSubmitting(true);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(heroForm)
      });

      if (response.ok) {
        setHeroSubmitted(true);
        setHeroForm({
          navn: '',
          telefon: '',
          email: '',
          ydelse: 'Tag & Facade',
          beskrivelse: '',
          website_url: ''
        });
      } else {
        setHeroError('Der opstod en fejl ved afsendelsen. Prøv venligst igen eller ring til os på 60 71 50 18.');
      }
    } catch (err) {
      console.error('Fejl ved indsendelse af formular:', err);
      setHeroError('Kunne ikke forbinde til serveren. Prøv venligst igen senere.');
    } finally {
      setHeroSubmitting(false);
    }
  };

  // Fetch gallery items & reviews from CMS
  useEffect(() => {
    // Fetch gallery preview
    fetch('https://api.dev.auxo.dk/public/v2/content/galleri?limit=12')
      .then(res => res.json())
      .then(data => {
        if (data && data.items) {
          const filtered = data.items
            .filter(item => {
              const url = Array.isArray(item.billede) ? item.billede[0] : (item.billede || '');
              return !url.includes('f0cee536-6343-45a6-8f18-8f142baa11a3') && item.id !== 'f0cee536-6343-45a6-8f18-8f142baa11a3';
            })
            .slice(0, 6);
          setGalleryItems(filtered);
        }
      })
      .catch(err => console.error('Fejl ved hentning af galleri:', err));

    // Fetch reviews
    fetch('https://api.dev.auxo.dk/public/content/anmeldelser')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setReviews(data);
        }
      })
      .catch(err => console.error('Fejl ved hentning af anmeldelser:', err));
  }, []);

  const heroImage = "https://assets.auxodev.dk/cdn-cgi/image/width=2000,quality=85,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/tegltag-integrerede-solceller-parcelhus.jpg";
  const carpenterWorkingImage = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/toemrerarbejde-tag-laegtekonstruktion.jpg";

  return (
    <>
      <SEO 
        isHome={true} 
        description="Fyns Tømreren er din tømrer på Fyn med base i Munkebo. Vi udfører tagarbejde, facade, træterrasser, køkkenmontering, sildebensparket og autoriseret asbestsanering for private og erhverv." 
        image={heroImage}
      />

      {/* Hero Section */}
      <section className="relative min-h-[650px] lg:min-h-[720px] flex items-center justify-center bg-[#1F2A37] text-white overflow-hidden py-12 lg:py-20">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Nyt tegltag med integrerede solceller på parcelhus – Tømrerarbejde på Fyn udført af Fyns Tømreren"
            className="w-full h-full object-cover object-center opacity-75 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F2A37]/80 via-[#1F2A37]/45 to-black/25"></div>
          {/* Subtle reference badge */}
          <div className="absolute bottom-4 right-4 z-10 hidden sm:flex items-center gap-1.5 px-3 py-1 bg-black/60 backdrop-blur-xs text-white/90 text-xs rounded-none border border-white/10">
            <i className="fa-solid fa-camera text-[10px] text-[#3A5A40]"></i>
            <span>Reference: Tegltag med solceller på Fyn</span>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Content Column */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 text-white text-xs font-bold uppercase tracking-wider mb-4">
                <i className="fa-solid fa-award text-[#d75d5a]"></i>
                <span>Faglig stolthed & lokalt håndværk</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-white mb-6">
                <span className="block text-white">Tømrer på Fyn</span>
                <span className="block text-xl sm:text-2xl lg:text-3xl font-normal text-gray-200 mt-2 sm:mt-3">
                  – når dit projekt kræver godt håndværk
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
                Fyns Tømreren er din tømrer på Fyn, når du ønsker en løsning, der er gennemtænkt fra første opmåling til den sidste afslutning. Vi har base i Munkebo og kører på hele Fyn for både private og erhverv. Du får personlig dialog, faglig rådgivning og klare aftaler om tid og pris.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 text-sm sm:text-base text-gray-200">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-white text-lg"></i>
                  <span>Tag- & Facaderenovering</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-white text-lg"></i>
                  <span>Køkken- & Inventarmontering</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-white text-lg"></i>
                  <span>Sildebensparket & Gulve</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-white text-lg"></i>
                  <span>Aftaler & Pris overholdes</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to="/kontakt"
                  className="bg-[#d75d5a] hover:bg-[#c24b48] text-white px-6 py-3.5 font-semibold text-base transition-all duration-300 hover:-translate-y-1 text-center inline-flex items-center justify-center gap-2 shadow-md hover:shadow-xl"
                >
                  <span>Kontakt os</span>
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
              </div>
            </div>

            {/* Right Webform Column */}
            <div className="lg:col-span-5 w-full">
              <div className="bg-white border border-gray-100 p-6 sm:p-8 shadow-2xl text-gray-900">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 tracking-tight uppercase">
                    Få et godt tilbud på dit projekt
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Udfyld formularen, så vender vi hurtigt tilbage med en skarp pris.
                  </p>
                </div>

                {heroSubmitted ? (
                  <div className="bg-green-50 border border-green-200 p-6 text-center animate-fadeIn">
                    <div className="w-12 h-12 bg-green-100 text-green-700 flex items-center justify-center mx-auto mb-3">
                      <i className="fa-solid fa-check text-xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-green-900 mb-1">Tak for din henvendelse!</h3>
                    <p className="text-sm text-green-800">
                      Mange tak! Vi har modtaget din besked og vender tilbage hurtigst muligt.
                    </p>
                    <button
                      type="button"
                      onClick={() => setHeroSubmitted(false)}
                      className="mt-4 text-xs text-[#3A5A40] font-semibold underline hover:text-[#2D4632]"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleHeroFormSubmit} className="space-y-4">
                    {/* Antispam Honeypot */}
                    <input
                      type="text"
                      name="website_url"
                      tabIndex={-1}
                      autoComplete="off"
                      className="sr-only absolute"
                      value={heroForm.website_url}
                      onChange={handleHeroFormChange}
                    />

                    {heroError && (
                      <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs">
                        {heroError}
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Navn <span className="text-[#d75d5a]">*</span>
                      </label>
                      <input
                        type="text"
                        name="navn"
                        required
                        placeholder="Dit fulde navn"
                        value={heroForm.navn}
                        onChange={handleHeroFormChange}
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3A5A40] focus:bg-white focus:border-transparent transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Telefonnummer <span className="text-[#d75d5a]">*</span>
                        </label>
                        <input
                          type="tel"
                          name="telefon"
                          required
                          placeholder="f.eks. 60 71 50 18"
                          value={heroForm.telefon}
                          onChange={handleHeroFormChange}
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3A5A40] focus:bg-white focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          E-mail <span className="text-[#d75d5a]">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="din@email.dk"
                          value={heroForm.email}
                          onChange={handleHeroFormChange}
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3A5A40] focus:bg-white focus:border-transparent transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Vælg ydelse
                      </label>
                      <select
                        name="ydelse"
                        value={heroForm.ydelse}
                        onChange={handleHeroFormChange}
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#3A5A40] focus:bg-white focus:border-transparent transition-all"
                      >
                        <option value="Tag & Facade">Tag & Facade</option>
                        <option value="Asbestsanering">Asbestsanering (Autoriseret)</option>
                        <option value="Interiør & Gulve">Interiør & Gulve</option>
                        <option value="Køkken & Inventar">Køkken & Inventar</option>
                        <option value="Andet tømrerarbejde">Andet tømrerarbejde</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Beskrivelse af opgaven
                      </label>
                      <textarea
                        name="beskrivelse"
                        rows={3}
                        placeholder="Fortæl os kort om din opgave..."
                        value={heroForm.beskrivelse}
                        onChange={handleHeroFormChange}
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3A5A40] focus:bg-white focus:border-transparent transition-all resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={heroSubmitting}
                      className="w-full bg-[#d75d5a] hover:bg-[#c24b48] text-white py-3.5 px-4 font-bold text-sm transition-all duration-300 hover:-translate-y-1 shadow-md hover:shadow-xl flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {heroSubmitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin"></i>
                          <span>Sender tilbudsanmodning...</span>
                        </>
                      ) : (
                        <>
                          <span>Send tilbudsanmodning</span>
                          <i className="fa-solid fa-paper-plane text-xs"></i>
                        </>
                      )}
                    </button>

                    <p className="text-[11px] text-center text-gray-500 pt-1">
                      Uforpligtende & gratis • Vi svarer typisk indenfor 24 timer
                    </p>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SEO Section with Intro */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Intro Grid: Image + Tømrerarbejde på Fyn Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
            
            {/* Image */}
            <ScrollReveal animation="slide-left" duration={800} className="relative">
              <figure className="relative overflow-hidden shadow-xl border border-gray-100 bg-white">
                <img
                  src={carpenterWorkingImage}
                  alt="Tømrermester i arbejde på lægtekonstruktion under tagrenovering på Fyn"
                  className="w-full h-[450px] object-cover"
                />
                <figcaption className="p-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <i className="fa-solid fa-hammer text-[#3A5A40]"></i>
                    <span>Tømrerarbejde på lægter og tagkonstruktion</span>
                  </span>
                  <span className="text-[11px] text-gray-500 font-medium">Kvalitetshåndværk på Fyn</span>
                </figcaption>
              </figure>
            </ScrollReveal>

            {/* Text Content: Tømrerarbejde på Fyn – hvad kan vi hjælpe dig med? */}
            <ScrollReveal animation="slide-right" duration={800} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#3A5A40]/10 text-[#3A5A40] text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-hammer"></i>
                <span>Tømrerarbejde på Fyn</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-[#111111] tracking-tight">
                Tømrerarbejde på Fyn – hvad kan vi hjælpe dig med?
              </h2>

              <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                Vi udfører tømrerarbejde på Fyn i forbindelse med både renovering, ombygning og nyetablering. Det kan være en mindre opgave i boligen eller et større projekt med tilbygning og totalentreprise. Vi rådgiver om materialer og konstruktioner, så løsningen passer til huset, dine behov og den fremtidige vedligeholdelse.
              </p>
              
              <div className="pt-4 grid grid-cols-2 gap-6 border-t border-gray-100">
                <div>
                  <div className="text-2xl font-bold text-[#1F2A37]">Lokal forankring</div>
                  <div className="text-sm text-gray-600 mt-1">Dækker Munkebo, Odense & hele Fyn</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1F2A37]">Snedkerpræcision</div>
                  <div className="text-sm text-gray-600 mt-1">Gennemførte løsninger ned i detaljen</div>
                </div>
              </div>

              <div className="pt-2 flex items-center gap-6">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-[#d75d5a] hover:bg-[#c24b48] text-white px-6 py-3 font-semibold text-sm transition-colors shadow-sm"
                >
                  <span>Indhent tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-2 text-[#1F2A37] font-semibold hover:text-[#3A5A40] transition-colors text-sm"
                >
                  <span>Læs mere om os</span>
                  <i className="fa-solid fa-chevron-right text-xs"></i>
                </Link>
              </div>
            </ScrollReveal>

          </div>

          {/* 2-Column Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            
            {/* Card 1: Udvendigt tømrerarbejde */}
            <ScrollReveal animation="fade-up" delay={150} className="h-full">
              <div className="bg-white border border-gray-200 p-6 sm:p-8 h-full flex flex-col justify-between hover:border-[#3A5A40]/40 transition-colors duration-300 shadow-xs">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                    <div className="w-10 h-10 bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center font-bold">
                      <i className="fa-solid fa-house-chimney text-lg"></i>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Tag • Facade • Terrasse
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-[#111111]">
                    Udvendigt tømrerarbejde, der passer på huset
                  </h3>

                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    Trænger taget til renovering, eller skal det udskiftes, arbejder vi med tegltag, skifertag og ståltag samt undertag, lægter, spær, tagvinduer og tagrender. Vi hjælper også med facaderenovering, facadebeklædning i træ eller komposit samt efterisolering af tag og ydervægge.
                  </p>

                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    Til udelivet bygger vi træterrasser og andre trækonstruktioner, der tilpasses boligen og omgivelserne. Her er korrekt opbygning og materialevalg afgørende for et godt resultat. Vi tager os også af tilbygninger, hvor de nye kvadratmeter skal hænge naturligt sammen med den eksisterende bygning.
                  </p>
                </div>

                <div className="pt-6 mt-4 border-t border-gray-100 flex flex-wrap gap-2">
                  <Link
                    to="/ydelser/tag-facade"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F2A37] hover:text-[#3A5A40] transition-colors"
                  >
                    <span>Læs om tag & facade</span>
                    <i className="fa-solid fa-arrow-right text-[10px]"></i>
                  </Link>
                </div>
              </div>
            </ScrollReveal>

            {/* Card 2: Detaljer i hjemmet */}
            <ScrollReveal animation="fade-up" delay={200} className="h-full">
              <div className="bg-white border border-gray-200 p-6 sm:p-8 h-full flex flex-col justify-between hover:border-[#3A5A40]/40 transition-colors duration-300 shadow-xs">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                    <div className="w-10 h-10 bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center font-bold">
                      <i className="fa-solid fa-ruler-combined text-lg"></i>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Snedkeri • Gulve • Inventar
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-[#111111]">
                    Detaljer i hjemmet, der skal passe præcist
                  </h3>

                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    Indendørs udfører vi præcist tømrer- og snedkerarbejde, herunder sildebensparket, trægulve, trætrapper, døre, paneler og akustikløsninger i træ. Vi sørger for nøjagtig tilpasning og pæne afslutninger, så materialer, samlinger og udtryk bliver en naturlig del af indretningen.
                  </p>

                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    Har du valgt et nyt køkken, kan vi stå for montering og tilpasning af elementer og bordplader. Vi fremstiller også specialinventar, køkkenøer, garderober, reoler og indbygningsskabe, når standardmål ikke udnytter pladsen optimalt, eller når du ønsker et mere sammenhængende udtryk.
                  </p>
                </div>

                <div className="pt-6 mt-4 border-t border-gray-100 flex flex-wrap gap-4">
                  <Link
                    to="/ydelser/interior-gulve"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F2A37] hover:text-[#3A5A40] transition-colors"
                  >
                    <span>Interiør & gulve</span>
                    <i className="fa-solid fa-arrow-right text-[10px]"></i>
                  </Link>
                  <span className="text-gray-300">|</span>
                  <Link
                    to="/ydelser/koekken-inventar"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F2A37] hover:text-[#3A5A40] transition-colors"
                  >
                    <span>Køkken & inventar</span>
                    <i className="fa-solid fa-arrow-right text-[10px]"></i>
                  </Link>
                </div>
              </div>
            </ScrollReveal>

            {/* Card 3: Asbestsanering på Fyn */}
            <ScrollReveal animation="fade-up" delay={250} className="h-full">
              <div className="bg-white border border-gray-200 p-6 sm:p-8 h-full flex flex-col justify-between hover:border-[#3A5A40]/40 transition-colors duration-300 shadow-xs">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                    <div className="w-10 h-10 bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center font-bold">
                      <i className="fa-solid fa-shield-halved text-lg"></i>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Autoriseret sanering
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-[#111111]">
                    Asbestsanering på Fyn – derfor er autoriseret hjælp vigtig
                  </h3>

                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    Asbest kræver særlig viden, det rette udstyr og en fast arbejdsgang, fordi støv og fibre ikke må spredes under nedtagningen. Fyns Tømreren udfører autoriseret asbestsanering på Fyn og kan hjælpe med asbestholdige bølgeeternitplader og skifereternit på bygningens tag.
                  </p>

                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    Vores asbestarbejde er autoriseret af Arbejdstilsynet. Ved håndtering af asbest sørger vi for lovpligtig anmeldelse, støvbegrænsning og korrekt emballering. Transport og aflevering på et godkendt deponi indgår efter den valgte løsning, så ansvar og omfang fremgår tydeligt af tilbuddet.
                  </p>

                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    Skal et gammelt asbesttag erstattes, kan vi samle sanering og opbygning af et nyt tag i samme projekt. Det giver en enkel overgang mellem sikker nedtagning og det efterfølgende tagarbejde. Vi tilbyder også udelukkende nedtagning, hvis det er den opgave, du har brug for hjælp til.
                  </p>
                </div>

                <div className="pt-6 mt-4 border-t border-gray-100 flex flex-wrap gap-2">
                  <Link
                    to="/ydelser/asbestsanering"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F2A37] hover:text-[#3A5A40] transition-colors"
                  >
                    <span>Læs mere om asbestsanering</span>
                    <i className="fa-solid fa-arrow-right text-[10px]"></i>
                  </Link>
                </div>
              </div>
            </ScrollReveal>

            {/* Card 4: Et gennemskueligt forløb fra første samtale */}
            <ScrollReveal animation="fade-up" delay={300} className="h-full">
              <div className="bg-white border border-gray-200 p-6 sm:p-8 h-full flex flex-col justify-between hover:border-[#3A5A40]/40 transition-colors duration-300 shadow-xs">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                    <div className="w-10 h-10 bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center font-bold">
                      <i className="fa-solid fa-handshake-angle text-lg"></i>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Tryghed & Gennemskuelighed
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-[#111111]">
                    Et gennemskueligt forløb fra første samtale
                  </h3>

                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    En god løsning begynder med, at vi taler om dine ønsker og de forhold, der kan påvirke arbejdet. Herefter kan vi rådgive om materialer, konstruktion og vedligeholdelse samt udarbejde et gennemskueligt tilbud. Under projektet holder vi dig orienteret om fremdriften.
                  </p>

                  <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                    Vi lægger vægt på nøjagtige opmålinger, præcis montering og gode byggematerialer. Samtidig beskytter vi de berørte områder og rydder op efter arbejdsdagen. Målet er et ordentligt forløb, hvor du ved, hvad der skal udføres, og hvad der er aftalt om tid og pris.
                  </p>

                  <div className="pt-2 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-[#1F2A37] font-medium">
                      <i className="fa-solid fa-check text-[#3A5A40]"></i>
                      <span>Fast aftale om tid og pris</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#1F2A37] font-medium">
                      <i className="fa-solid fa-check text-[#3A5A40]"></i>
                      <span>Løbende dialog og orientering</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#1F2A37] font-medium">
                      <i className="fa-solid fa-check text-[#3A5A40]"></i>
                      <span>Grundig oprydning og respekt for dit hjem</span>
                    </div>
                  </div>
                </div>

                <div className="pt-6 mt-4 border-t border-gray-100 flex flex-wrap gap-2">
                  <Link
                    to="/om-os"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F2A37] hover:text-[#3A5A40] transition-colors"
                  >
                    <span>Læs mere om vores værdier</span>
                    <i className="fa-solid fa-arrow-right text-[10px]"></i>
                  </Link>
                </div>
              </div>
            </ScrollReveal>

          </div>

          {/* Full-width Local Area Card: Din tømrer på Fyn med base i Munkebo */}
          <ScrollReveal animation="fade-up" delay={350}>
            <div className="bg-[#1F2A37] text-white p-8 sm:p-10 shadow-lg border border-gray-800">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                
                <div className="lg:col-span-8 space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 text-white text-xs font-bold uppercase tracking-wider">
                    <i className="fa-solid fa-location-dot text-[#d75d5a]"></i>
                    <span>Lokal forankring på hele Fyn</span>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                    Din tømrer på Fyn med base i Munkebo
                  </h3>

                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Fra vores base i Munkebo kører vi ud til tømrerarbejde i Odense og på resten af Fyn, herunder Kerteminde, Nyborg, Svendborg og Middelfart. Vi kommer gerne ud og ser nærmere på opgaven, så rådgivning og tilbud kan tage udgangspunkt i boligen og dine konkrete ønsker.
                  </p>

                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    Søger du en tømrer på Fyn til tag, facade, gulve, køkken, terrasse, tilbygning eller autoriseret asbestsanering, er du velkommen til at kontakte os. Fortæl os om dit projekt, så tager vi en faglig og uforpligtende snak om mulighederne og det næste skridt.
                  </p>

                  {/* City Badges */}
                  <div className="pt-2 flex flex-wrap gap-2">
                    {['Munkebo', 'Odense', 'Kerteminde', 'Nyborg', 'Svendborg', 'Middelfart', 'Hele Fyn'].map((by, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-white/10 hover:bg-white/15 text-xs font-semibold text-gray-200 tracking-wide transition-colors"
                      >
                        <i className="fa-solid fa-check text-[#3A5A40] mr-1.5 text-[10px]"></i>
                        {by}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-4 flex flex-col gap-3 justify-center items-start lg:items-end border-t lg:border-t-0 lg:border-l border-white/10 pt-6 lg:pt-0 lg:pl-8">
                  <Link
                    to="/kontakt"
                    className="w-full sm:w-auto bg-[#d75d5a] hover:bg-[#c24b48] text-white px-6 py-3.5 font-bold text-sm text-center inline-flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5 shadow-md"
                  >
                    <span>Tag en uforpligtende snak</span>
                    <i className="fa-solid fa-arrow-right text-xs"></i>
                  </Link>
                  <a
                    href="tel:60715018"
                    className="w-full sm:w-auto bg-transparent border border-white/30 hover:border-white text-white px-6 py-3.5 font-bold text-sm text-center inline-flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <i className="fa-solid fa-phone text-[#3A5A40]"></i>
                    <span>Ring 60 71 50 18</span>
                  </a>
                </div>

              </div>
            </div>
          </ScrollReveal>

        </div>
      </section>

      {/* Ydelser Section - Modern Card / Box Grid */}
      <section className="relative w-full bg-[#1e1e1e] py-16 lg:py-24 text-white overflow-hidden">
        
        {/* Floating Stacked Circular Action Buttons on Middle-Right Edge */}
        <div className="hidden sm:flex absolute right-6 top-1/2 -translate-y-1/2 z-30 flex-col gap-3">
          <a
            href="tel:60715018"
            title="Ring til os"
            className="w-12 h-12 bg-[#3A5A40] text-white flex items-center justify-center text-lg shadow-xl hover:bg-[#2d4632] hover:scale-110 transition-all duration-300"
          >
            <i className="fa-solid fa-phone"></i>
          </a>
          <Link
            to="/kontakt"
            title="Indhent tilbud"
            className="w-12 h-12 bg-[#d75d5a] text-white flex items-center justify-center text-lg shadow-xl hover:bg-[#c24b48] hover:scale-110 transition-all duration-300"
          >
            <i className="fa-solid fa-arrow-right"></i>
          </Link>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Heading */}
          <ScrollReveal animation="fade-up" className="text-left max-w-2xl mb-12 space-y-3">
            <h2 className="text-3xl sm:text-4xl font-extrabold uppercase tracking-wide text-white">
              Vores Fagområder
            </h2>
            <p className="text-gray-300 text-sm sm:text-base">
              Professionelle tømrerløsninger og kvalitetsarbejde til hele Fyn
            </p>
          </ScrollReveal>

          {/* 4 Box Grid (col-6 layout: 2 columns on desktop) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            
            {/* Box 1: Tag & Facade */}
            <ScrollReveal animation="fade-up" delay={0} className="h-full">
              <Link 
                to="/ydelser/tag-facade" 
                className="group bg-[#2a2a2a] border border-gray-800 overflow-hidden flex flex-col justify-between hover:border-[#3A5A40] transition-all duration-300 shadow-lg hover:shadow-2xl h-full"
              >
                <div>
                  <figure className="relative h-56 overflow-hidden">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/tagrenovering-undertag-tagvinduer.jpg"
                      alt="Tagrenovering med nyt undertag og tagvinduer på Fyn"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-300"></div>
                    <figcaption className="absolute bottom-3 left-3 bg-[#1F2A37]/85 backdrop-blur-xs text-white text-[11px] px-2.5 py-1 flex items-center gap-1.5 shadow-sm">
                      <i className="fa-solid fa-house-chimney text-[#3A5A40]"></i>
                      <span>Reference: Tagrenovering & undertag</span>
                    </figcaption>
                  </figure>
                  <div className="p-6 space-y-3">
                    <h3 className="text-xl font-bold uppercase tracking-wide text-white group-hover:text-[#d75d5a] transition-colors duration-300">
                      Tag & Facade
                    </h3>
                    <p className="text-sm text-gray-300 line-clamp-3 leading-relaxed">
                      Nyt tag, tagrenovering, efterisolering samt eksklusiv facadebeklædning i træ og komposit.
                    </p>
                  </div>
                </div>
                <div className="px-6 pb-6 pt-2">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-200 group-hover:text-[#d75d5a] transition-colors duration-300">
                    Læs mere <i className="fa-solid fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
                  </span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Box 2: Interiør & Gulve */}
            <ScrollReveal animation="fade-up" delay={150} className="h-full">
              <Link 
                to="/ydelser/interior-gulve" 
                className="group bg-[#2a2a2a] border border-gray-800 overflow-hidden flex flex-col justify-between hover:border-[#3A5A40] transition-all duration-300 shadow-lg hover:shadow-2xl h-full"
              >
                <div>
                  <figure className="relative h-56 overflow-hidden">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/totalrenovering-fritlagte-loftsbjaelker.jpg"
                      alt="Sildebensparket i egetræ lagt med præcision i bolig på Fyn"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-300"></div>
                    <figcaption className="absolute bottom-3 left-3 bg-[#1F2A37]/85 backdrop-blur-xs text-white text-[11px] px-2.5 py-1 flex items-center gap-1.5 shadow-sm">
                      <i className="fa-solid fa-layer-group text-[#3A5A40]"></i>
                      <span>Reference: Sildebensparket i egetræ</span>
                    </figcaption>
                  </figure>
                  <div className="p-6 space-y-3">
                    <h3 className="text-xl font-bold uppercase tracking-wide text-white group-hover:text-[#d75d5a] transition-colors duration-300">
                      Interiør & Gulve
                    </h3>
                    <p className="text-sm text-gray-300 line-clamp-3 leading-relaxed">
                      Sildebensparket, trægulve, opbygning af trætrapper, døre, paneler og akustikløsninger.
                    </p>
                  </div>
                </div>
                <div className="px-6 pb-6 pt-2">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-200 group-hover:text-[#d75d5a] transition-colors duration-300">
                    Læs mere <i className="fa-solid fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
                  </span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Box 3: Køkken & Inventar */}
            <ScrollReveal animation="fade-up" delay={300} className="h-full">
              <Link 
                to="/ydelser/koekken-inventar" 
                className="group bg-[#2a2a2a] border border-gray-800 overflow-hidden flex flex-col justify-between hover:border-[#3A5A40] transition-all duration-300 shadow-lg hover:shadow-2xl h-full"
              >
                <div>
                  <figure className="relative h-56 overflow-hidden">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/moderne-hvidt-koekken-hoejskabe-hvidevarer.jpg"
                      alt="Montering og tilpasning af nyt køkken og skabselementer"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-300"></div>
                    <figcaption className="absolute bottom-3 left-3 bg-[#1F2A37]/85 backdrop-blur-xs text-white text-[11px] px-2.5 py-1 flex items-center gap-1.5 shadow-sm">
                      <i className="fa-solid fa-utensils text-[#3A5A40]"></i>
                      <span>Reference: Køkken- & skabsmontering</span>
                    </figcaption>
                  </figure>
                  <div className="p-6 space-y-3">
                    <h3 className="text-xl font-bold uppercase tracking-wide text-white group-hover:text-[#d75d5a] transition-colors duration-300">
                      Køkken & Inventar
                    </h3>
                    <p className="text-sm text-gray-300 line-clamp-3 leading-relaxed">
                      Montering af nye køkkener, specialfremstillet inventar, garderober og skabsløsninger.
                    </p>
                  </div>
                </div>
                <div className="px-6 pb-6 pt-2">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-200 group-hover:text-[#d75d5a] transition-colors duration-300">
                    Læs mere <i className="fa-solid fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
                  </span>
                </div>
              </Link>
            </ScrollReveal>

            {/* Box 4: Totalentreprise & Terrasser */}
            <ScrollReveal animation="fade-up" delay={450} className="h-full">
              <Link 
                to="/kontakt" 
                className="group bg-[#2a2a2a] border border-gray-800 overflow-hidden flex flex-col justify-between hover:border-[#3A5A40] transition-all duration-300 shadow-lg hover:shadow-2xl h-full"
              >
                <div>
                  <figure className="relative h-56 overflow-hidden">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/faerdigmonteret-traeterrasse-bolig.jpg"
                      alt="Færdigbygget træterrasse og totalentreprise ved privat bolig på Fyn"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-300"></div>
                    <figcaption className="absolute bottom-3 left-3 bg-[#1F2A37]/85 backdrop-blur-xs text-white text-[11px] px-2.5 py-1 flex items-center gap-1.5 shadow-sm">
                      <i className="fa-solid fa-tree text-[#3A5A40]"></i>
                      <span>Reference: Opbygning af træterrasse</span>
                    </figcaption>
                  </figure>
                  <div className="p-6 space-y-3">
                    <h3 className="text-xl font-bold uppercase tracking-wide text-white group-hover:text-[#d75d5a] transition-colors duration-300">
                      Totalentreprise & Terrasser
                    </h3>
                    <p className="text-sm text-gray-300 line-clamp-3 leading-relaxed">
                      Komplet styring af dit byggeprojekt, træterrasser, tilbygninger og koordinering af faggrupper.
                    </p>
                  </div>
                </div>
                <div className="px-6 pb-6 pt-2">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-200 group-hover:text-[#d75d5a] transition-colors duration-300">
                    Læs mere <i className="fa-solid fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
                  </span>
                </div>
              </Link>
            </ScrollReveal>

          </div>
        </div>
      </section>

      {/* Asbestsanering Section - Clean Image & Text Split */}
      <section className="py-20 bg-white overflow-hidden border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Text Content */}
            <ScrollReveal animation="slide-left" duration={800} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#d75d5a]/10 border border-[#d75d5a]/30 text-[#d75d5a] text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-shield-halved"></i>
                <span>Lovkrav 2025 • Autoriseret asbestsanering</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-[#111111] tracking-tight">
                Sikker og autoriseret asbestsanering på hele Fyn
              </h2>
              
              <p className="text-gray-700 leading-relaxed text-base">
                Skal du have udskiftet et ældre bølgeeternittag eller saneret asbestholdige byggematerialer? Siden 1. januar 2025 kræver lovgivningen, at nedtagning af asbest udelukkende må udføres af virksomheder med officiel autorisation.
              </p>
              
              <p className="text-gray-700 leading-relaxed text-base">
                Hos Fyns - Tømreren ApS er vi certificerede og godkendt af Arbejdstilsynet. Vi håndterer hele forløbet trygt og forskriftsmæssigt – fra lovpligtig indberetning og støvfri nedtagning til miljøgodkendt deponering og genopbygning med nyt tag.
              </p>
              
              <div className="pt-4 grid grid-cols-2 gap-6 border-t border-gray-100">
                <div>
                  <div className="text-2xl font-bold text-[#1F2A37]">100% Lovlig</div>
                  <div className="text-sm text-gray-600 mt-1">Officiel autorisation fra Arbejdstilsynet</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#1F2A37]">Totalløsning</div>
                  <div className="text-sm text-gray-600 mt-1">Sanering, bortskaffelse & nyt tag i ét</div>
                </div>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/ydelser/asbestsanering"
                  className="inline-flex items-center gap-2 text-[#1F2A37] font-semibold hover:text-[#d75d5a] transition-colors"
                >
                  Læs mere om asbestsanering
                  <i className="fa-solid fa-chevron-right text-xs"></i>
                </Link>
                <span className="text-gray-300">|</span>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-[#3A5A40] font-semibold hover:text-[#2d4632] transition-colors"
                >
                  Indhent tilbud
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
              </div>
            </ScrollReveal>

            {/* Image */}
            <ScrollReveal animation="slide-right" duration={800} className="relative">
              <figure className="relative overflow-hidden shadow-xl border border-gray-100 bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/933ba291-d562-4801-b20a-fae6de561b45/tagkonstruktion-laegtearbejde-renovering.jpg"
                  alt="Tagkonstruktion og lægtearbejde under autoriseret asbestsanering og tagudskiftning på Fyn"
                  className="w-full h-[450px] object-cover"
                />
                <div className="absolute top-4 left-4 bg-[#1F2A37]/90 backdrop-blur-xs text-white text-xs font-bold px-3 py-1.5 flex items-center gap-2 border border-white/10 shadow-md">
                  <i className="fa-solid fa-shield-halved text-[#d75d5a]"></i>
                  <span>Autoriseret virksomhed</span>
                </div>
                <figcaption className="p-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600 flex items-center justify-between">
                  <span>Autoriseret håndtering og opbygning af tag efter asbestsanering</span>
                  <span className="text-[#3A5A40] font-semibold flex items-center gap-1 text-[11px]"><i className="fa-solid fa-circle-check"></i> Godkendt</span>
                </figcaption>
              </figure>
            </ScrollReveal>

          </div>
        </div>
      </section>

      {/* Hvorfor vælge os (USP'er med ikoner) */}
      <section className="py-20 bg-white border-y border-gray-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <ScrollReveal animation="fade-up" className="text-center max-w-2xl mx-auto mb-16 space-y-2">
            <h2 className="text-3xl font-bold text-[#111111]">
              Hvorfor vælge Fyns - Tømreren ApS?
            </h2>
            <p className="text-gray-600 text-sm">
              Din tryghed og et holdbart resultat er altid i højsædet hos os.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* USP 1 */}
            <ScrollReveal animation="fade-up" delay={0} className="text-center space-y-3">
              <div className="text-[#3A5A40] text-3xl">
                <i className="fa-regular fa-award"></i>
              </div>
              <h3 className="text-lg font-bold text-[#1F2A37]">Kvalitetshåndværk</h3>
            </ScrollReveal>

            {/* USP 2 */}
            <ScrollReveal animation="fade-up" delay={150} className="text-center space-y-3">
              <div className="text-[#3A5A40] text-3xl">
                <i className="fa-regular fa-location-dot"></i>
              </div>
              <h3 className="text-lg font-bold text-[#1F2A37]">Lokal tømrer på Fyn</h3>
            </ScrollReveal>

            {/* USP 3 */}
            <ScrollReveal animation="fade-up" delay={300} className="text-center space-y-3">
              <div className="text-[#3A5A40] text-3xl">
                <i className="fa-regular fa-comments"></i>
              </div>
              <h3 className="text-lg font-bold text-[#1F2A37]">Personlig service</h3>
            </ScrollReveal>

            {/* USP 4 */}
            <ScrollReveal animation="fade-up" delay={450} className="text-center space-y-3">
              <div className="text-[#3A5A40] text-3xl">
                <i className="fa-regular fa-toolbox"></i>
              </div>
              <h3 className="text-lg font-bold text-[#1F2A37]">Mange års erfaring</h3>
            </ScrollReveal>

          </div>

        </div>
      </section>

      {/* Galleri Preview (6 Billeder + Lightbox) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="text-3xl font-bold text-[#111111]">
                Billeder fra nyeste projekter
              </h2>
            </div>
            <Link
              to="/galleri"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F2A37] hover:text-[#3A5A40] transition-colors"
            >
              Se hele galleriet
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
          </div>

          {/* Grid mit 6 images */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryItems.map((item, idx) => {
              const rawImg = Array.isArray(item.billede) ? item.billede[0] : (item.billede || item.url || '');
              const resizedUrl = rawImg ? rawImg.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/') : '';

              return (
                <ScrollReveal
                  key={item.id || idx}
                  animation="scale-up"
                  delay={idx * 100}
                  onClick={() => setLightboxIndex(idx)}
                  className="group bg-white overflow-hidden cursor-pointer shadow-xs hover:shadow-lg transition-all duration-300 border border-gray-200 flex flex-col"
                >
                  <figure className="flex flex-col h-full">
                    <div className="relative h-56 overflow-hidden bg-gray-100">
                      <img
                        src={resizedUrl}
                        alt={`${item.titel || 'Projekt'} – ${item.beskrivelse || 'Tømrerarbejde på Fyn udført af Fyns Tømreren'}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-3 right-3 w-8 h-8 bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="fa-solid fa-expand text-xs"></i>
                      </div>
                    </div>
                    <figcaption className="p-3.5 bg-gray-50 border-t border-gray-100 flex-1">
                      <div className="text-xs font-bold text-[#111111] line-clamp-1 group-hover:text-[#3A5A40] transition-colors">
                        {item.titel}
                      </div>
                      {item.beskrivelse && (
                        <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
                          {item.beskrivelse}
                        </p>
                      )}
                    </figcaption>
                  </figure>
                </ScrollReveal>
              );
            })}
          </div>

        </div>
      </section>

      {/* Lightbox for Gallery Preview */}
      {lightboxIndex !== null && (
        <Lightbox
          images={galleryItems}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : galleryItems.length - 1))}
          onNext={() => setLightboxIndex((prev) => (prev < galleryItems.length - 1 ? prev + 1 : 0))}
        />
      )}

      {/* Anmeldelser */}
      <section className="py-20 bg-[#F5F5F5] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header with Title & Arrow Navigation */}
          <ScrollReveal animation="fade-up" className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
            <div>
              <h2 className="text-3xl font-bold text-[#111111]">
                Det siger vores kunder på Fyn
              </h2>
            </div>

            {/* Navigation Arrows */}
            <div className="flex items-center gap-1">
              <button
                onClick={handlePrevReviews}
                aria-label="Forrige anmeldelse"
                className="w-10 h-10 text-gray-700 flex items-center justify-center hover:text-[#3A5A40] transition-colors duration-200"
              >
                <i className="fa-solid fa-chevron-left text-base"></i>
              </button>
              <button
                onClick={handleNextReviews}
                aria-label="Næste anmeldelse"
                className="w-10 h-10 text-gray-700 flex items-center justify-center hover:text-[#3A5A40] transition-colors duration-200"
              >
                <i className="fa-solid fa-chevron-right text-base"></i>
              </button>
            </div>
          </ScrollReveal>

          {/* Slider aligned strictly to container line */}
          <div
            ref={reviewsRef}
            className="flex gap-6 overflow-x-auto scroll-smooth pb-4 [scrollbar-width:none] [-ms-overflow-style:none]"
          >
            {(reviews.length > 0 ? reviews : defaultReviews).map((rev, index) => (
              <div
                key={index}
                className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] flex-shrink-0 bg-white p-6 shadow-sm border border-gray-100 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center gap-1 text-amber-400 text-sm mb-3">
                    {[...Array(rev.stjerner || 5)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star"></i>
                    ))}
                  </div>
                  {rev.overskrift && (
                    <h3 className="font-semibold text-base text-[#111111] mb-2">
                      {rev.overskrift}
                    </h3>
                  )}
                  <p className="text-gray-700 text-sm leading-relaxed italic mb-4">
                    "{rev.tekst}"
                  </p>
                </div>
                <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="font-bold text-sm text-[#1F2A37]">{rev.navn}</span>
                  <span className="text-xs text-gray-500 font-medium">{rev.by}</span>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>



      {/* CTA Section */}
      <section className="py-16 bg-[#1F2A37] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Klar til dit næste projekt?
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-base">
            Kontakt os i dag for en uforpligtende snak om dine muligheder eller for at modtage et specifik tilbud på dit tømrerarbejde.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="bg-[#d75d5a] hover:bg-[#c24b48] text-white px-8 py-3.5 font-semibold text-base transition-all duration-300 hover:-translate-y-1 shadow-md hover:shadow-xl inline-flex items-center gap-2"
            >
              Indhent tilbud
              <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
            <a
              href="tel:60715018"
              className="border border-white bg-transparent hover:bg-transparent text-white px-8 py-3.5 font-semibold text-base transition-all duration-300 hover:-translate-y-1 shadow-md hover:shadow-xl whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-white"></i>
              <span>60 71 50 18</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
